<script lang="ts" setup>
import { ref, watch, computed, defineProps, defineEmits, onMounted, onUnmounted } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { BulkOrderInfo, BulkOrderInfoType } from '../domains/valueObjects/BulkOrderInfo';
import NumberInputWithComma from '@/corePagesModule/components/NumberInputWithComma.vue';
import { i18n } from '@/i18n';

const t = i18n.t;

/**
 * 選択された値を表す型
 */
type Options = {
  text: string;
  value: BulkOrderInfo;
};

const props = defineProps<{
  index: number;
  modelValue: BulkOrderInfo;
  placeholder: string;
  disabled?: boolean;
}>();

const orderAmount = ref<number>(0);

const options = computed((): Options[] => {
  const amount = orderAmount.value ?? 0;
  return [
    {
      text: `${amount.addComma()}${t('以上')}`,
      value: {
        amount,
        type: BulkOrderInfoType.MIN,
      },
    },
    {
      text: `${amount.addComma()}${t('以下')}`,
      value: {
        amount,
        type: BulkOrderInfoType.MAX,
      },
    },
    {
      text: `${amount.addComma()}`,
      value: {
        amount,
        type: BulkOrderInfoType.IS,
      },
    },
  ];
});

const selected = ref<BulkOrderInfo>(props.modelValue);
const selectedText = ref<string>('');
const isOpen = ref(false);

const emit = defineEmits<{
  (e: 'update:modelValue', value: BulkOrderInfo): void;
}>();

watch(
  () => props.modelValue,
  () => {
    selected.value = props.modelValue;
  }
);

/**
 * placeholderを表示するかどうか
 */
const isPlaceholder = computed(() => {
  return selected.value.amount === 0 || selected.value === null || selected.value === undefined;
});

/**
 * ドロップダウンの表示状態を切り替えます。
 */
const toggleDropdown = () => {
  if (!props.disabled) {
    isOpen.value = !isOpen.value;
  }
};

/**
 * オプションを選択し、親コンポーネントに選択値を通知します。
 * @param option - 選択されたオプション
 */
const selectOption = (option: Options) => {
  if (option.value.amount === 0) return;
  selected.value = option.value;
  selectedText.value = option.text;
  emit('update:modelValue', option.value);
  isOpen.value = false;
};

/**
 * ドロップダウンを閉じます。
 * @param event - クリックイベント
 */
const closeDropdown = (event: MouseEvent) => {
  if (!isOpen.value) return;
  const target = event.target;
  const selector = `#order-status-select-${props.index}`;
  const arrowSelector = `#order-status-select-arrow-${props.index}`;
  if (!(target instanceof Element)) return;
  if (target.closest(arrowSelector)) return;
  if (target.closest(selector)) return;

  isOpen.value = false;
};

/**
 * onInputイベントハンドラ
 * @param value - 入力値
 */
const onInput = (value: string) => {
  orderAmount.value = Number(value);
  if (orderAmount.value === 0) {
    const resetValue = {
      amount: 0,
      type: BulkOrderInfoType.MIN,
    };

    selected.value = resetValue;
    selectedText.value = '';
    emit('update:modelValue', resetValue);
  }
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});
</script>

<template>
  <div class="select-box_container">
    <div class="select-box__label">
      <p class="select-box__label-text">{{ t('まとめ発注') }}</p>
      <div class="select-box__modal-container">
        <SvgLoader class="select-box__question-icon" name="questionIcon" />
        <div class="select-box__modal">
          <h1 class="select-box__modal-title">{{ t('まとめ発注とは') }}</h1>
          <p class="select-box__modal-text">
            {{ t('まとめ発注は、') }}
            <b class="select-box__modal-text--bold">{{ t('仕入先と発注数量のルールが定まっている際に使用します。') }}</b>
            {{ t('同日納期かつ特定の仕入先と置場向けの複数発注の数量をまとめて規定の数量以上、以下、ちょうどを判断します。') }}<br />
            {{
              t(
                '例えば関東で製造している仕入先Aと九州倉庫向けは10tonで発注するというルールが存在した場合、複数発注をまとめて10tonとなっているか確認する場合に使用します。'
              )
            }}
            {{ t('まとめ発注欄で数量と条件を指定することで単一日ごとに条件に合致していない場合はアラートが表示されます。') }}
          </p>
        </div>
      </div>
    </div>
    <div :id="`order-status-select-${index}`" class="select-box">
      <div class="select-box__selected" :class="{ 'select-box__selected--disabled': disabled }" @click="toggleDropdown">
        <div
          :class="`select-box__field ${isPlaceholder ? 'select-box__field--placeholder' : ''} ${
            disabled ? 'select-box__selected--disabled' : ''
          }`"
        >
          <!-- 選択肢した値を表示する。何も選択していない場合はplaceholderの文言を表示する -->
          {{ isPlaceholder ? placeholder : selectedText }}
        </div>
        <SvgLoader v-show="!isOpen" :id="`order-status-select-arrow-${index}`" name="arrowDown" class="select-box__arrow" />
        <SvgLoader v-show="isOpen" name="arrowUp" class="select-box__arrow" />
      </div>
      <div v-show="isOpen" class="select-box__dropdown">
        <div class="select-box__option">
          <NumberInputWithComma
            :model-value="orderAmount"
            type="number"
            class="select-box__input"
            :placeholder="t('数量を記入してください')"
            @update:model-value="(e) => (orderAmount = e)"
            @input="onInput"
          />
        </div>
        <div v-for="option in options" :key="option.value.amount" class="select-box__option" @click="selectOption(option)">
          {{ option.text }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.select-box {
  justify-items: center;
  position: relative;
  user-select: none;

  &__container {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    gap: 0;
  }

  &__question-icon {
    width: 15px;
    height: 15px;
  }

  &__label {
    display: grid;
    grid-template-columns: auto 18px;
    gap: 3px;
    justify-content: start;

    &-text {
      padding: 8px 0;
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 18.2px;
      text-align: center;

      color: $textDefault;
    }
  }

  &__modal {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 2px;
    width: max-content;
    padding: 16px 24px;
    display: none;
    gap: 16px;
    z-index: 1000;
    background-color: $semanticWhite;
    box-shadow: 0px 0px 22px 0px #80808066;
    border-radius: 8px;

    &-container:hover &,
    &:hover {
      display: grid;
    }

    &-container {
      position: relative;
      padding: 8px 0;
    }

    &-title {
      font-family: Noto Sans JP;
      font-size: 16px;
      font-weight: 700;
      line-height: 16px;
      letter-spacing: 0em;
      text-align: center;
      color: $textDefault;
    }

    &-text {
      width: 378px;
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 20.27px;
      letter-spacing: 0em;
      text-align: left;
      color: $textDefault;

      &--bold {
        font-weight: 700;
        color: $semanticPrimary;
      }
    }
  }

  &__field {
    height: 20px;

    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;
    background-color: $semanticWhite;

    &--placeholder {
      color: $textDisable;
    }
  }

  &__selected {
    width: 100%;
    color: white;
    cursor: pointer;
    display: grid;
    grid-template-columns: 1fr 24px;
    text-align: center;
    align-items: center;
    padding: 6px 8px;
    background-color: $semanticWhite;

    &--disabled {
      background-color: $surfaceSecondary;
    }
  }

  &__arrow {
    display: grid;
    align-content: center;
    justify-content: center;
    height: 24px;
    width: 24px;
  }

  &__dropdown {
    position: absolute;
    width: 100%;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1;
    overflow-y: auto;
  }

  &__input {
    width: 100%;
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;
    padding: 6px 8px;
    border-radius: 4px;
    border: 1px solid $borderDefault;

    &:focus,
    &:hover {
      border-color: $semanticPrimary;
    }

    &::placeholder {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0em;
      text-align: left;
      text-overflow: ellipsis;
      white-space: nowrap;

      color: $textDisable;
    }
  }

  &__option {
    padding: 10px;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: $semanticPrimarySubdued;
    }

    &--disabled {
      color: #ccc;
      cursor: not-allowed;
    }
  }
}
</style>
